import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { api } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";

interface EmailLogSummary {
  failure: number;
  success: number;
  window_hours: number;
}

export function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [emailFailureCount, setEmailFailureCount] = useState<number>(0);

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  // メール送信失敗バッジ用: 60 秒ごとに summary を取得
  useEffect(() => {
    let cancelled = false;
    const fetchSummary = () => {
      api
        .get<EmailLogSummary>("/admin/email-logs/summary", {
          params: { hours: 24 },
        })
        .then((res) => {
          if (!cancelled) setEmailFailureCount(res.data.failure);
        })
        .catch(() => {
          // 通信失敗時はバッジに 0 を出す (false negative を避けるためログだけ残す)
          if (!cancelled) setEmailFailureCount(0);
        });
    };
    fetchSummary();
    const id = setInterval(fetchSummary, 60_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  const navClass = ({ isActive }: { isActive: boolean }) =>
    `px-3 py-2 rounded-md text-sm font-medium ${
      isActive ? "bg-gray-900 text-white" : "text-gray-300 hover:bg-gray-800 hover:text-white"
    }`;

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <header className="bg-gray-950 text-white sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16 flex-wrap">
          <Link to="/admin" className="text-lg font-semibold flex items-center gap-2">
            かんたん在庫 <span className="text-xs bg-red-600 px-2 py-0.5 rounded">管理</span>
          </Link>
          <nav className="flex items-center gap-1 flex-wrap">
            <NavLink to="/admin/orders" className={navClass}>
              発注管理
            </NavLink>
            <NavLink to="/admin/applications" className={navClass}>
              申込管理
            </NavLink>
            <NavLink to="/admin/tenants" className={navClass}>
              顧客管理
            </NavLink>
            <NavLink to="/admin/inventory" className={navClass}>
              在庫・商品
            </NavLink>
            <NavLink to="/admin/settings" className={navClass}>
              項目設定
            </NavLink>
            <NavLink to="/admin/demand-log" className={navClass}>
              需要ヒートマップ
            </NavLink>
            <NavLink to="/admin/email-logs" className={navClass}>
              メールログ
              {emailFailureCount > 0 && (
                <span className="ml-1 inline-flex items-center justify-center rounded-full bg-red-600 text-white text-xs px-1.5 py-0.5">
                  {emailFailureCount}
                </span>
              )}
            </NavLink>
          </nav>
          <div className="flex items-center gap-3 text-sm">
            <span className="hidden sm:block text-gray-300">{user?.email}</span>
            <button type="button" className="btn-secondary" onClick={handleLogout}>
              ログアウト
            </button>
          </div>
        </div>
      </header>
      {emailFailureCount > 0 && (
        <div className="bg-red-50 border-b border-red-200 text-red-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 text-sm">
            ⚠️ <strong>直近 24 時間でメール送信に {emailFailureCount} 件失敗しています。</strong>{" "}
            <Link to="/admin/email-logs" className="underline">
              詳細を確認
            </Link>
          </div>
        </div>
      )}
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Outlet />
      </main>
      <footer className="py-6 border-t border-gray-200 text-center text-xs text-gray-500">
        © かんたん在庫 / 管理画面
      </footer>
    </div>
  );
}
